/**
 * PDP add-on block: swap gallery image URLs only (no DOM rebuild), bundled cart add.
 */
;(function () {
  'use strict'

  /** Matches theme Product `classes.hidden` / Slideshow thumb selector `:not(.hide)`. */
  var GALLERY_HIDE_CLASS = 'hide'

  /**
   * @param {string} sectionId
   * @returns {object|undefined}
   */
  function getProductSectionInstance(sectionId) {
    if (!window.theme || !theme.sections || !theme.sections.instances) return undefined
    return theme.sections.instances.find(function (inst) {
      return String(inst.id) === String(sectionId) && inst.type === 'product'
    })
  }

  function makeBundleId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID()
    }
    return String(Date.now()) + '-' + String(Math.random()).slice(2)
  }

  /**
   * @param {HTMLImageElement|null} img
   * @returns {object|null}
   */
  function readImgState(img) {
    if (!img) return null
    return {
      src: img.getAttribute('src'),
      srcset: img.getAttribute('srcset'),
      sizes: img.getAttribute('sizes'),
      alt: img.getAttribute('alt'),
      width: img.getAttribute('width'),
      height: img.getAttribute('height'),
      dataSrc: img.getAttribute('data-src'),
      dataSrcset: img.getAttribute('data-srcset'),
      dataPhotoswipeSrc: img.getAttribute('data-photoswipe-src'),
      dataPhotoswipeWidth: img.getAttribute('data-photoswipe-width'),
      dataPhotoswipeHeight: img.getAttribute('data-photoswipe-height'),
      className: img.className,
    }
  }

  /**
   * @param {HTMLImageElement|null} img
   * @param {object|null} state
   */
  function writeImgState(img, state) {
    if (!img || !state) return
    function setOrRemove(attr, val) {
      if (val != null && val !== '') img.setAttribute(attr, val)
      else img.removeAttribute(attr)
    }
    setOrRemove('src', state.src)
    setOrRemove('srcset', state.srcset)
    setOrRemove('sizes', state.sizes)
    setOrRemove('alt', state.alt)
    setOrRemove('width', state.width)
    setOrRemove('height', state.height)
    setOrRemove('data-src', state.dataSrc)
    setOrRemove('data-srcset', state.dataSrcset)
    setOrRemove('data-photoswipe-src', state.dataPhotoswipeSrc)
    setOrRemove('data-photoswipe-width', state.dataPhotoswipeWidth)
    setOrRemove('data-photoswipe-height', state.dataPhotoswipeHeight)
    img.className = state.className || ''
  }

  /**
   * @param {object|undefined} instance - theme Product section instance
   */
  function reinitProductGallery(instance) {
    if (!instance || typeof instance.initProductSlider !== 'function') return
    // Start on first visible cell; avoids animating from main product featured index → 0 (flash / odd motion)
    instance.initProductSlider(undefined, { forceInitialIndex: 0 })
  }

  /**
   * @param {HTMLElement} mainSlider
   * @param {HTMLElement|null} thumbScroller
   */
  function clearAddonGallerySlotHides(mainSlider, thumbScroller) {
    mainSlider.querySelectorAll('.product-main-slide').forEach(function (slide) {
      slide.classList.remove(GALLERY_HIDE_CLASS)
    })
    if (thumbScroller) {
      thumbScroller.querySelectorAll('.product__thumb-item').forEach(function (item) {
        item.classList.remove(GALLERY_HIDE_CLASS)
      })
    }
  }

  /**
   * Main gallery slide is treated as “video” → excluded from add-on image index (hidden like it does not exist).
   * @param {HTMLElement} slide - `.product-main-slide`
   * @returns {boolean}
   */
  function isVideoMainSlide(slide) {
    return !!(
      slide.querySelector('.product__video-wrapper') ||
      slide.querySelector('.product-video-trigger')
    )
  }

  /**
   * Thumbnail row: play icon = video thumb, same index rule as main.
   * @param {HTMLElement} item - `.product__thumb-item`
   * @returns {boolean}
   */
  function isVideoThumbItem(item) {
    return !!item.querySelector('.product__thumb-icon .icon-play')
  }

  /**
   * @param {object} product - JSON product with media[] (src, srcset, zoomSrc, alt, aspectRatio)
   * @param {HTMLElement} mainSlider
   * @param {HTMLElement|null} thumbScroller
   * @param {object|undefined} instance - theme Product section instance
   */
  function applyAddonImages(product, mainSlider, thumbScroller, instance) {
    var media = product.media || []
    if (!media.length && product.featuredSrc) {
      media = [
        {
          src: product.featuredSrc,
          srcset: product.featuredSrcset || '',
          zoomSrc: product.featuredZoomSrc || product.featuredSrc,
          alt: product.title,
          aspectRatio: 1,
        },
      ]
    }
    if (!media.length) return

    var allSlides = Array.from(mainSlider.querySelectorAll('.product-main-slide'))
    var imageSlides = allSlides.filter(function (slide) {
      return !isVideoMainSlide(slide)
    })

    allSlides.forEach(function (slide) {
      if (isVideoMainSlide(slide)) {
        slide.classList.add(GALLERY_HIDE_CLASS)
      }
    })

    imageSlides.forEach(function (slide, j) {
      if (j < media.length) {
        slide.classList.remove(GALLERY_HIDE_CLASS)
        var m = media[j]
        var img = slide.querySelector('img')
        var wrap = slide.querySelector('.image-wrap')
        if (!img || !m.src) return

        img.setAttribute('src', m.src)
        if (m.srcset) {
          img.setAttribute('srcset', m.srcset)
        } else {
          img.removeAttribute('srcset')
        }
        img.removeAttribute('data-src')
        img.removeAttribute('data-srcset')
        if (m.alt) img.setAttribute('alt', m.alt)
        if (m.zoomSrc) {
          img.setAttribute('data-photoswipe-src', m.zoomSrc)
        }
        if (m.width) img.setAttribute('width', String(m.width))
        if (m.height) img.setAttribute('height', String(m.height))

        if (wrap && m.aspectRatio != null && m.aspectRatio !== '') {
          var ar = parseFloat(String(m.aspectRatio))
          if (!isNaN(ar) && ar > 0) {
            wrap.style.paddingBottom = 100 / ar + '%'
          }
        }
      } else {
        slide.classList.add(GALLERY_HIDE_CLASS)
      }
    })

    if (thumbScroller) {
      var allThumbs = Array.from(thumbScroller.querySelectorAll('.product__thumb-item'))
      var imageThumbs = allThumbs.filter(function (item) {
        return !isVideoThumbItem(item)
      })
      allThumbs.forEach(function (item) {
        if (isVideoThumbItem(item)) {
          item.classList.add(GALLERY_HIDE_CLASS)
        }
      })
      imageThumbs.forEach(function (item, j) {
        if (j < media.length) {
          item.classList.remove(GALLERY_HIDE_CLASS)
          var m = media[j]
          var img = item.querySelector('img')
          if (!m || !m.src || !img) return
          img.setAttribute('src', m.src)
          if (m.srcset) {
            img.setAttribute('srcset', m.srcset)
          } else {
            img.removeAttribute('srcset')
          }
          img.removeAttribute('data-src')
          img.removeAttribute('data-srcset')
          if (m.alt) img.setAttribute('alt', m.alt)
          var wrap = img.closest('.image-wrap')
          if (wrap && m.aspectRatio != null && m.aspectRatio !== '') {
            var ar2 = parseFloat(String(m.aspectRatio))
            if (!isNaN(ar2) && ar2 > 0) {
              wrap.style.paddingBottom = 100 / ar2 + '%'
            }
          }
        } else {
          item.classList.add(GALLERY_HIDE_CLASS)
        }
      })
    }

    reinitProductGallery(instance)
  }

  /**
   * @param {HTMLElement} root
   */
  function initBlock(root) {
    if (root.dataset.addonInitialized === 'true') {
      return
    }
    root.dataset.addonInitialized = 'true'

    var sectionId = root.getAttribute('data-section-id')
    var productId = root.getAttribute('data-product-id')
    var formId = root.getAttribute('data-form-id')
    if (!sectionId || !formId) return

    var jsonEl = root.querySelector('[data-addon-json]')
    if (!jsonEl) return

    var data
    try {
      data = JSON.parse(jsonEl.textContent)
    } catch (e) {
      return
    }

    var products = data.products || []
    if (!products.length) return

    var sectionEl = document.getElementById('ProductSection-' + sectionId + '-' + productId)
    if (!sectionEl) return

    var mainSlider = sectionEl.querySelector('[data-product-photos]')
    var thumbScroller = sectionEl.querySelector('.product__thumbs--scroller')

    if (!mainSlider) return

    /** @type {{ main: Array<{ img: object|null, wrapStyle: string|null }>, thumbs: Array<object|null>, thumbWrapStyles: Array<string|null> }|null} */
    var gallerySnapshot = null

    function captureGallerySnapshot() {
      if (gallerySnapshot) return
      gallerySnapshot = { main: [], thumbs: [], thumbWrapStyles: [] }
      mainSlider.querySelectorAll('.product-main-slide').forEach(function (slide) {
        var img = slide.querySelector('img')
        var wrap = slide.querySelector('.image-wrap')
        gallerySnapshot.main.push({
          img: readImgState(img),
          wrapStyle: wrap ? wrap.getAttribute('style') : null,
        })
      })
      if (thumbScroller) {
        thumbScroller.querySelectorAll('.product__thumb-item img').forEach(function (img) {
          gallerySnapshot.thumbs.push(readImgState(img))
        })
        thumbScroller.querySelectorAll('.product__thumb-item .image-wrap').forEach(function (wrap) {
          gallerySnapshot.thumbWrapStyles.push(wrap.getAttribute('style'))
        })
      }
    }

    function restoreGalleryFromSnapshot() {
      if (!gallerySnapshot) return
      clearAddonGallerySlotHides(mainSlider, thumbScroller)
      var slides = mainSlider.querySelectorAll('.product-main-slide')
      gallerySnapshot.main.forEach(function (entry, i) {
        var slide = slides[i]
        if (!slide) return
        var img = slide.querySelector('img')
        var wrap = slide.querySelector('.image-wrap')
        writeImgState(img, entry.img)
        if (wrap) {
          if (entry.wrapStyle != null) wrap.setAttribute('style', entry.wrapStyle)
          else wrap.removeAttribute('style')
        }
      })
      if (thumbScroller && gallerySnapshot.thumbs) {
        var timgs = thumbScroller.querySelectorAll('.product__thumb-item img')
        var twraps = thumbScroller.querySelectorAll('.product__thumb-item .image-wrap')
        gallerySnapshot.thumbs.forEach(function (st, i) {
          writeImgState(timgs[i], st)
        })
        if (gallerySnapshot.thumbWrapStyles) {
          gallerySnapshot.thumbWrapStyles.forEach(function (st, i) {
            if (twraps[i]) {
              if (st != null) twraps[i].setAttribute('style', st)
              else twraps[i].removeAttribute('style')
            }
          })
        }
      }
      reinitProductGallery(getProductSectionInstance(sectionId))
    }

    captureGallerySnapshot()

    var form = document.getElementById(formId)
    if (!form) return

    var addonCheckboxes = root.querySelectorAll('.product-addon__input[type="checkbox"]')

    /**
     * Updates the PDP gallery from the single selected add-on, if any (none → restore main product).
     */
    function syncGalleryFromAddonCheckboxes() {
      var checked = root.querySelector('.product-addon__input[type="checkbox"]:checked')
      if (!checked) {
        applyGallery(-1)
        return
      }
      var idx = parseInt(checked.value, 10)
      if (isNaN(idx)) {
        applyGallery(-1)
        return
      }
      applyGallery(idx)
    }

    function syncAddonVisibility() {
      root.hidden = false
      root.removeAttribute('aria-hidden')
    }

    syncAddonVisibility()

    function applyGallery(productIndex) {
      var instance = getProductSectionInstance(sectionId)
      captureGallerySnapshot()

      if (productIndex < 0) {
        restoreGalleryFromSnapshot()
        return
      }

      var product = products[productIndex]
      if (!product) {
        restoreGalleryFromSnapshot()
        return
      }

      applyAddonImages(product, mainSlider, thumbScroller, instance)
    }

    addonCheckboxes.forEach(function (cb) {
      cb.addEventListener('change', function () {
        if (cb.checked) {
          addonCheckboxes.forEach(function (other) {
            if (other !== cb) {
              other.checked = false
            }
          })
        }
        syncGalleryFromAddonCheckboxes()
      })
    })

    form.addEventListener(
      'submit',
      function (evt) {
        var selectedBoxes = root.querySelectorAll('.product-addon__input[type="checkbox"]:checked')
        if (!selectedBoxes.length) {
          return
        }

        var indices = []
        selectedBoxes.forEach(function (box) {
          var idx = parseInt(box.value, 10)
          if (!isNaN(idx) && products[idx] && products[idx].variantId) {
            indices.push(idx)
          }
        })
        if (!indices.length) {
          return
        }

        evt.preventDefault()
        evt.stopImmediatePropagation()

        var variantInput = form.querySelector('[name="id"]')
        var qtyInput = form.querySelector('[name="quantity"]')
        var mainVariantId = variantInput ? variantInput.value : ''
        var qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1

        if (!mainVariantId) {
          return
        }

        var titles = indices.map(function (i) {
          return products[i].title
        })
        var addonLine = titles.join(', ')
        var primaryAddon = products[indices[0]] || null

        var bundleId = makeBundleId()
        var parentProductUrl = (root.getAttribute('data-parent-product-url') || '').trim()
        var btn = form.querySelector('[data-add-to-cart]')
        if (btn) {
          btn.classList.add('btn--loading')
        }

        var items = [
          {
            id: parseInt(mainVariantId, 10),
            quantity: qty,
            properties: {
              Addon: addonLine,
              _bundle_id: bundleId,
              _addon_image: primaryAddon && primaryAddon.featuredSrc ? primaryAddon.featuredSrc : '',
              _addon_price:
                primaryAddon && primaryAddon.price != null ? String(primaryAddon.price) : '',
            },
          },
        ]
        indices.forEach(function (idx) {
          var addon = products[idx]
          var childProps = {
            _addon_bundle_child: 'true',
            _bundle_id: bundleId,
          }
          if (parentProductUrl) {
            childProps._addon_parent_url = parentProductUrl
          }
          items.push({
            id: parseInt(addon.variantId, 10),
            quantity: qty,
            properties: childProps,
          })
        })

        fetch(theme.routes.cartAdd, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({ items: items }),
        })
          .then(function (res) {
            return res.json()
          })
          .then(function (resData) {
            if (resData.status === 422 || resData.errors) {
              var msg =
                (typeof resData.description === 'string' && resData.description) ||
                (typeof resData.message === 'string' && resData.message) ||
                (typeof resData.errors === 'string' && resData.errors) ||
                'Could not add to cart'
              alert(msg)
              return
            }

            form.dispatchEvent(
              new CustomEvent('ajaxProduct:added', {
                detail: { product: resData },
                bubbles: true,
              })
            )

            if (theme.settings && theme.settings.cartType === 'page') {
              window.location = theme.routes.cartPage
            }
          })
          .catch(function () {
            alert('Could not add to cart')
          })
          .finally(function () {
            if (btn) {
              btn.classList.remove('btn--loading')
            }
          })
      },
      true
    )
  }

  function initAll() {
    document.querySelectorAll('[data-product-addon]').forEach(initBlock)
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll)
  } else {
    initAll()
  }

  document.addEventListener('shopify:section:load', function (evt) {
    if (evt.target && evt.target.querySelector && evt.target.querySelector('[data-product-addon]')) {
      initAll()
    }
  })
})()
